// ONE persistent style, never setStyle(): the OpenFreeMap Liberty vector style
// loads once and its layers form the "vector basemap" group; the other
// OpenFreeMap styles (Light, Dark) are hidden layer groups added beside it, and
// every raster basemap is a pre-registered hidden layer at the bottom of the
// stack. Switching toggles visibility — overlays, sources and handlers all survive.
//
// State is PER MAP: the Studio is a second MapLibre instance with the same
// picker. Its style carries no basemap of its own, so there every vector
// basemap — Liberty included — is a group fetched the first time it is picked
// (imagery is what the Studio opens on; a vector style it never shows is a
// download for nothing).

import * as maplibregl from 'maplibre-gl';

export const OFM_STYLE = 'https://tiles.openfreemap.org/styles/liberty';

interface RasterDef {
  tiles: string[];
  tileSize?: number;
  maxzoom?: number;
  attribution?: string;
}

const sub = (template: string, subs: string[]) => subs.map((s) => template.replace('{s}', s));

// Read only in a browser: the tests import this module under node.
const ORIGIN = typeof location === 'undefined' ? '' : location.origin;

export const RASTER_BASEMAPS: Record<string, RasterDef> = {
  street: { tiles: sub('https://{s}.google.com/vt/lyrs=m&hl=en&x={x}&y={y}&z={z}', ['mt0', 'mt1', 'mt2', 'mt3']), maxzoom: 20, attribution: '© Google' },
  terrain: { tiles: sub('https://{s}.google.com/vt/lyrs=p&hl=en&x={x}&y={y}&z={z}', ['mt0', 'mt1', 'mt2', 'mt3']), maxzoom: 20, attribution: '© Google' },
  googleHybrid: { tiles: sub('https://{s}.google.com/vt/lyrs=y&hl=en&x={x}&y={y}&z={z}', ['mt0', 'mt1', 'mt2', 'mt3']), maxzoom: 20, attribution: '© Google' },
  googleSat: { tiles: sub('https://{s}.google.com/vt/lyrs=s&hl=en&x={x}&y={y}&z={z}', ['mt0', 'mt1', 'mt2', 'mt3']), maxzoom: 20, attribution: '© Google' },
  osm: { tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'], maxzoom: 19, attribution: '© OpenStreetMap contributors' },
  topo: { tiles: sub('https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', ['a', 'b', 'c']), maxzoom: 17, attribution: '© OpenTopoMap' },
  esriStreet: { tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}'], maxzoom: 19, attribution: '© Esri' },
  // Via the backend proxy: Esri's imagery stops at a depth that varies by
  // place (z17 over open desert, z19 over cities) and past it Esri serves a
  // grey "Map data not yet available" tile. The proxy serves the deepest tile
  // Esri has there, enlarged, so zooming in keeps the imagery.
  esri: { tiles: [`${ORIGIN}/api/tile/esri/{z}/{x}/{y}`], maxzoom: 19, attribution: '© Esri' },
  // MapLibre supports {quadkey} natively — the old BingLayer subclass dies here.
  bing: { tiles: ['0', '1', '2', '3'].map((s) => `https://ecn.t${s}.tiles.virtualearth.net/tiles/a{quadkey}.jpeg?g=1&n=z`), maxzoom: 19, attribution: '© Microsoft' },
  // Via the backend proxy, which reprojects Yandex's EPSG:3395 tiles to the
  // EPSG:3857 MapLibre requires — loaded directly they'd sit up to ~20 km off
  // in latitude. Yandex imagery over the Middle East is often fresher/sharper
  // than Google's, which is the point of carrying it.
  yandex: { tiles: [`${ORIGIN}/api/tile/yandex/{z}/{x}/{y}`], maxzoom: 19, attribution: '© Yandex' },
  s2cloudless: { tiles: ['https://tiles.maps.eox.at/wmts/1.0.0/s2cloudless-2024_3857/default/g/{z}/{y}/{x}.jpg'], maxzoom: 17, attribution: 'Sentinel-2 cloudless © EOX — contains modified Copernicus data' },
  // NASA GIBS: TODAY's VIIRS true-colour pass ("default" date = latest) —
  // coarse (z9 cap) but live smoke plumes and burn scars, which no archival
  // basemap shows.
  gibs: { tiles: ['https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/VIIRS_SNPP_CorrectedReflectance_TrueColor/default/default/GoogleMapsCompatible_Level9/{z}/{y}/{x}.jpg'], maxzoom: 9, attribution: '© NASA EOSDIS GIBS' },
};

// Light and Dark used to be CARTO's light_all/dark_all rasters. CARTO now
// stamps "API KEY REQUIRED" across every keyless tile of both (seen 2026-09-15,
// whatever the Referer), and dark mode pairs the map with Dark — so both are
// OpenFreeMap styles instead: keyless, and drawn from the same planet tiles,
// sprite and glyph server Liberty already loaded. Each style is fetched once
// at install and its layers read Liberty's sources (identical ids and URLs
// across OpenFreeMap's styles), so a switch costs no second tile download.
// The keys stay `light`/`dark`: admin defaults and saved sheets name them.
export const VECTOR_BASEMAPS: Record<string, string> = {
  light: 'https://tiles.openfreemap.org/styles/positron',
  dark: 'https://tiles.openfreemap.org/styles/dark',
};

export const BASEMAP_LABELS: Record<string, string> = {
  ofm: 'Vector (OpenFreeMap)', street: 'Google Street', terrain: 'Terrain',
  googleHybrid: 'Google Hybrid', googleSat: 'Google Satellite', osm: 'OSM',
  light: 'Light', dark: 'Dark', topo: 'Topo', esriStreet: 'Esri Street',
  esri: 'Esri Imagery', bing: 'Bing Aerial', yandex: 'Yandex Satellite',
  s2cloudless: 'Sentinel-2 Cloudless', gibs: 'NASA Today (VIIRS)',
};

// The dark style names images its own sprite doesn't carry: `circle-11` is
// `circle_11` there, and there is no `wood-pattern` at all (the wood fill's
// colour still draws without it). Left alone, every town label logs a
// missing image and draws without its dot.
const IMAGE_RENAMES: Record<string, string> = { 'circle-11': 'circle_11' };
const MISSING_PATTERNS = new Set(['wood-pattern']);
const PATTERN_PROPS = ['fill-pattern', 'line-pattern', 'background-pattern'];

export interface StyleLayer {
  id: string;
  layout?: Record<string, unknown>;
  paint?: Record<string, unknown>;
  [key: string]: unknown;
}

const renameImages = (v: unknown): unknown =>
  typeof v === 'string' ? IMAGE_RENAMES[v] ?? v
    : Array.isArray(v) ? v.map(renameImages) : v;

export const vectorLayerId = (key: string, id: string): string => `base-${key}:${id}`;

// A fetched style's layers as a hidden group: namespaced (the styles share
// ids like `water` with Liberty), hidden, and pointed at images that exist.
export function vectorBasemapLayers(key: string, layers: StyleLayer[]): StyleLayer[] {
  return layers.map((layer) => {
    const layout: Record<string, unknown> = { ...layer.layout, visibility: 'none' };
    if ('icon-image' in layout) layout['icon-image'] = renameImages(layout['icon-image']);
    const paint: Record<string, unknown> = { ...layer.paint };
    for (const prop of PATTERN_PROPS) {
      if (typeof paint[prop] === 'string' && MISSING_PATTERNS.has(paint[prop] as string)) delete paint[prop];
    }
    return { ...layer, id: vectorLayerId(key, layer.id), layout, paint };
  });
}

// 3D terrain. One global raster-DEM source (AWS open "Terrain Tiles", Terrarium
// encoding — free, keyless, worldwide, ~90 m). maplibre's TerrainControl toggles
// it on/off; TERRAIN_EXAGGERATION is the vertical scale it applies. Reads best on
// the globe projection, where the exaggerated relief is visible edge-on.
export const TERRAIN_SOURCE_ID = 'terrain-dem';
export const TERRAIN_EXAGGERATION = 2;

export function installTerrain(map: maplibregl.Map): void {
  if (map.getSource(TERRAIN_SOURCE_ID)) return;
  map.addSource(TERRAIN_SOURCE_ID, {
    type: 'raster-dem',
    tiles: ['https://elevation-tiles-prod.s3.amazonaws.com/terrarium/{z}/{x}/{y}.png'],
    encoding: 'terrarium',
    tileSize: 256,
    maxzoom: 15,
    attribution: '© Mapzen · Terrain Tiles (AWS Open Data)',
  });
}

interface BasemapState {
  /** The style's own layers — Liberty, i.e. `ofm`, on the cockpit. Empty on a
   *  map whose style has no basemap, where `ofm` is fetched like Light/Dark. */
  ownIds: string[];
  /** Layer ids per vector basemap, set once its style has loaded. */
  variants: Record<string, string[]>;
  loading: Set<string>;
  current: string;
}
const states = new WeakMap<maplibregl.Map, BasemapState>();

const rasterId = (key: string): string => `base-${key}`;

/** The style a vector basemap is fetched from, or null for a raster or for
 *  the map's own style. */
function vectorUrl(st: BasemapState, key: string): string | null {
  if (key === 'ofm') return st.ownIds.length ? null : OFM_STYLE;
  return VECTOR_BASEMAPS[key] ?? null;
}

export function installBasemaps(map: maplibregl.Map, opts: { ownStyle?: boolean } = {}): void {
  const own = opts.ownStyle ?? true;
  const st: BasemapState = {
    ownIds: own ? map.getStyle().layers.map((l) => l.id) : [],
    variants: {}, loading: new Set(), current: 'ofm',
  };
  states.set(map, st);
  const bottom = map.getStyle().layers[0]?.id;
  for (const [key, def] of Object.entries(RASTER_BASEMAPS)) {
    map.addSource(rasterId(key), {
      type: 'raster', tiles: def.tiles, tileSize: def.tileSize ?? 256,
      maxzoom: def.maxzoom, attribution: def.attribution,
    });
    map.addLayer({
      id: rasterId(key), type: 'raster', source: rasterId(key),
      layout: { visibility: 'none' },
      // No cross-fade: tiles pop in the instant they arrive instead of easing
      // over 300 ms, which is most of what makes basemap loading feel sluggish.
      paint: { 'raster-fade-duration': 0 },
    }, bottom);
  }
  // With a style of its own the map shows Liberty, so Light and Dark load up
  // front (dark mode flips to Dark); without one, a vector basemap loads when
  // it is picked.
  if (own) {
    for (const key of Object.keys(VECTOR_BASEMAPS)) void installVectorBasemap(map, st, key);
  }
}

/** Where a vector basemap's layers go: under Liberty on the cockpit, and on a
 *  map without it, under the first layer that is not a basemap — the Studio's
 *  scenes, overlays and markup all ride above it. */
function vectorAnchor(map: maplibregl.Map, st: BasemapState): string | undefined {
  const liberty = st.ownIds[0];
  if (liberty !== undefined) return map.getLayer(liberty) ? liberty : undefined;
  const basemap = new Set([
    ...Object.keys(RASTER_BASEMAPS).map(rasterId), ...Object.values(st.variants).flat(),
  ]);
  return map.getStyle().layers.find((l) => !basemap.has(l.id))?.id;
}

async function installVectorBasemap(
  map: maplibregl.Map, st: BasemapState, key: string,
): Promise<void> {
  const url = vectorUrl(st, key);
  if (!url || st.variants[key] || st.loading.has(key)) return;
  st.loading.add(key);
  let style: {
    sources: Record<string, maplibregl.SourceSpecification>; layers: StyleLayer[]; sprite?: string;
  };
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    style = await res.json();
  } catch (err) {
    console.warn(`Basemap "${key}" did not load; ${
      st.ownIds.length ? 'Liberty stands in for it' : 'the current basemap stays'}.`, err);
    return;
  } finally {
    st.loading.delete(key);
  }
  if (states.get(map) !== st || !map.style) return;   // the map went away meanwhile
  for (const [id, source] of Object.entries(style.sources)) {
    if (!map.getSource(id)) map.addSource(id, source);
  }
  // A map with no style of its own has no sprite either. The OpenFreeMap
  // styles all name the same one, so the first to land brings it.
  if (typeof style.sprite === 'string' && map.getSprite().length === 0) map.setSprite(style.sprite);
  const layers = vectorBasemapLayers(key, style.layers);
  const anchor = vectorAnchor(map, st);
  for (const layer of layers) map.addLayer(layer as never, anchor);
  st.variants[key] = layers.map((l) => l.id);
  if (st.current === key) setBasemap(map, key);
}

export function setBasemap(map: maplibregl.Map, key: string): void {
  const st = states.get(map);
  if (!st) return;
  if (!(key in RASTER_BASEMAPS) && !(key in VECTOR_BASEMAPS) && key !== 'ofm') key = 'ofm';
  st.current = key;
  let shown = key;
  if (vectorUrl(st, key) && !st.variants[key]) {
    void installVectorBasemap(map, st, key);
    // Until its layers land: Liberty meanwhile where the map has it, and
    // otherwise whatever is showing now — installVectorBasemap swaps it in.
    if (!st.ownIds.length) return;
    shown = 'ofm';
  }
  for (const id of st.ownIds) {
    if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', shown === 'ofm' ? 'visible' : 'none');
  }
  for (const [k, ids] of Object.entries(st.variants)) {
    for (const id of ids) {
      if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', k === shown ? 'visible' : 'none');
    }
  }
  for (const k of Object.keys(RASTER_BASEMAPS)) {
    map.setLayoutProperty(rasterId(k), 'visibility', k === shown ? 'visible' : 'none');
  }
}
